import { useEffect, useState } from 'react';
import { FaCashRegister } from 'react-icons/fa';
import { toastSuccess } from '../utils/toasts';
import AdminSectionTitle from '../components/AdminSectionTitle';
import FilterTable, { BaseData } from '../components/Table/Table';
import useGetSelectedBusiness from '../hooks/useGetSelectedBusiness';
import Button from '../components/atoms/Button';
import { useGetBusinessCashRegistersQuery, useDeleteBusinessCashRegisterMutation } from '../store';
import CustomModal from '../components/atoms/CustomModal';
import MenuDropdown from '../components/atoms/MenuDropdown';
import { BusinessCashRegisterDTO } from '../models/business';
import CreateEditCashRegister from '../components/CreateEditCashRegister';
import EmptyTable from '../components/Table/EmptyTable';
import { IconPlay2 } from '../components/icons';

interface CashRegisterRow extends BaseData {
  active: JSX.Element;
  name: string;
}

const CashRegisterPage = () => {
  const { business } = useGetSelectedBusiness();
  const { data: cashRegisters } = useGetBusinessCashRegistersQuery({ businessId: business?.id || '' }, { skip: !business?.id });
  const [deleteCashRegister, { isLoading: isDeletingCashRegister, isSuccess: isDeleteSuccess }] = useDeleteBusinessCashRegisterMutation();
  const [showModal, setShowModal] = useState(false);
  const [modalBody, setModalBody] = useState<JSX.Element | null>(null);

  useEffect(() => {
    if (isDeleteSuccess) {
      toastSuccess('Caja eliminada correctamente');
      setShowModal(false);
    }
  }, [isDeleteSuccess]);

  const handleCreate = () => {
    setModalBody(
      <div className='flex flex-col max-w-[600px]'>
        <h1>Crear nueva caja</h1>
        <p className='text-text-tertiary'>Una vez creada, podrán realizar ventas</p>
        <CreateEditCashRegister businessId={business?.id || ''} onSuccess={() => {
          setShowModal(false);
        }} />
      </div>,
    );
    setShowModal(true);
  };

  const handleEdit = (cashRegister: BusinessCashRegisterDTO) => {
    setModalBody(
      <div className='flex flex-col max-w-[600px]'>
        <h1>Editar caja</h1>
        <p className='text-text-tertiary'>Una vez editada, podrán realizar ventas</p>
        <CreateEditCashRegister cashRegister={cashRegister} businessId={business?.id || ''} onSuccess={() => {
          setShowModal(false);
        }} />
      </div>,
    );
    setShowModal(true);
  };

  const handleDelete = (cashRegister: BusinessCashRegisterDTO) => {
    setModalBody(
      <div className='flex flex-col max-w-[600px]'>
        <h1>Eliminar caja</h1>
        <p className='text-text-tertiary'>Una vez eliminada, se eliminará de la lista</p>
        <div className='flex flex-row gap-4 mt-4'>
          <Button variant='secondary' onClick={() => { setShowModal(false); }}>Cancelar</Button>
          <Button loading={isDeletingCashRegister} onClick={() => {
            deleteCashRegister({ businessId: business?.id || '', cashRegisterId: cashRegister.id });
          }}>Eliminar</Button>
        </div>
      </div>,
    );
    setShowModal(true);
  };

  const rows: CashRegisterRow[] = cashRegisters?.map((cashRegister) => ({
    id: cashRegister.id,
    active: cashRegister.is_active ? (
      <div className='tag-primary'>
        Activa
      </div>
    ) : (
      <div className='tag-warning'>
        Inactiva
      </div>
    ),
    name: cashRegister.name,
  })) || [];

  const headCells = [
    {
      id: 'active',
      label: 'Estado',
    },
    {
      id: 'name',
      label: 'Nombre de la caja',
    },
  ];

  const customButton = <Button onClick={handleCreate} variant="secondary">
    <div className='flex flex-row gap-2 items-center'>
      <FaCashRegister className='min-w-[20px] min-h-[20px] w-5 h-5' />
      <p className='whitespace-nowrap hidden md:block'>Crear caja</p>
    </div>
  </Button>;

  const actions = (row: CashRegisterRow) => (
    <MenuDropdown<BusinessCashRegisterDTO>
      options={[
        {
          label: 'Editar caja',
          onClick: (cashRegister: BusinessCashRegisterDTO) => handleEdit(cashRegister),
        },
        {
          label: 'Eliminar caja',
          onClick: (cashRegister: BusinessCashRegisterDTO) => handleDelete(cashRegister),
        },
      ]}
      data={cashRegisters?.find((c) => c.id === row.id) ?? cashRegisters?.[0] ?? {} as BusinessCashRegisterDTO}
    />
  );

  return (
    <div className='my-6 flex flex-col gap-8'>
      <AdminSectionTitle
        title={`${business?.name}`}
        icon={<IconPlay2 className='w-12 h-12 rotate-180' />}
      />
      <div className='glass-surface'>
        <h1 className='text-2xl font-medium mb-2'>Cajas</h1>
        {cashRegisters?.length && cashRegisters?.length > 0 ? (
          <FilterTable
            rows={rows}
            headCells={headCells}
            actions={actions}
            customButtons={[customButton]}
            tableId="cash-register"
            pagination={{ enabled: true, pageSize: 10 }}
          />
        ) : (
          <EmptyTable
            button={[
              {
                label: 'Crear caja',
                onClick: handleCreate,
                icon: FaCashRegister,
                variant: 'secondary',
              },
            ]}
            title="No hay cajas"
            info="Crea una caja para poder realizar ventas"
            icon={FaCashRegister}
            iconColor="text-primary-500"
          />
        )}
      </div>
      <CustomModal
        isOpen={showModal}
        onRequestClose={() => setShowModal(false)}
        hideCancelButton
      >
        {modalBody}
      </CustomModal>
    </div>
  );
};

export default CashRegisterPage;
